import { useEffect, useState } from 'react'
import {
  Slide,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GetAppIcon from '@mui/icons-material/GetApp'
import IosShareIcon from '@mui/icons-material/IosShare'
import { useTranslation } from 'react-i18next'
import { useStandaloneMode } from '../hooks/useStandaloneMode'
import { useInstallPrompt } from '../hooks/useInstallPrompt'

const ENGAGEMENT_DELAY_MS = 5_000

/**
 * Dismissible install banner. Renders nothing on desktop, in PWA standalone
 * mode, or when the user has dismissed within the last 14 days. Slides in
 * 5 seconds after mount so it doesn't punch a first-time visitor in the face.
 *
 * iOS Safari has no programmatic install API — on iOS we show the share
 * icon + manual "Add to Home Screen" instructions instead of an Install CTA.
 */
export function InstallPrompt() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { isStandalone } = useStandaloneMode()
  const { canInstall, isIos, promptInstall, dismiss } = useInstallPrompt()
  const [delayed, setDelayed] = useState(false)
  // Local `visible` flag (vs the hook's persistent `dismissed`) lets the
  // Slide animate out before the consumer-side `dismiss()` flips localStorage
  // and unmounts us.
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const timer = window.setTimeout(() => setDelayed(true), ENGAGEMENT_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [])

  if (isDesktop || isStandalone || !canInstall || !delayed) return null

  const handleDismiss = () => {
    setVisible(false)
  }

  const handleInstall = () => {
    setVisible(false)
    void promptInstall()
  }

  return (
    <Slide
      direction="down"
      in={visible}
      mountOnEnter
      unmountOnExit
      onExited={() => {
        // Persist dismissal only after the exit animation completes so the
        // user sees the slide-out, not an abrupt unmount.
        dismiss()
      }}
    >
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          // Sit 8px below the AppBar. AppBar is dense on xs (48px), regular
          // on sm+ (56px); both branches add safe-top so this clears the
          // iOS notch correctly. Keep in sync with Layout.tsx Toolbar minHeight.
          top: {
            xs: 'calc(48px + var(--safe-top, 0px) + 8px)',
            sm: 'calc(56px + var(--safe-top, 0px) + 8px)',
          },
          // Strings so MUI doesn't multiply by theme.spacing (which would
          // turn 8 into 64px and leave a giant gutter on each side).
          left: '8px',
          right: '8px',
          zIndex: theme.zIndex.appBar + 2,
          p: 1.5,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {/* iOS branch shows the actual Share icon so users recognise what
              to tap. Android branch shows the generic download arrow. */}
          {isIos ? (
            <IosShareIcon color="primary" sx={{ flexShrink: 0 }} />
          ) : (
            <GetAppIcon color="primary" sx={{ flexShrink: 0 }} />
          )}
          <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('install.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {isIos ? t('install.iosBody') : t('install.androidBody')}
            </Typography>
          </Stack>
          {!isIos && (
            <Button
              variant="contained"
              onClick={handleInstall}
              sx={{ flexShrink: 0, minHeight: 44 }}
            >
              {t('install.cta')}
            </Button>
          )}
          <IconButton
            onClick={handleDismiss}
            aria-label={t('install.dismiss')}
            sx={{ flexShrink: 0, width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Paper>
    </Slide>
  )
}
